import { ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useClassNames } from "@/hooks/useClassNames";
import { LayoutContext } from "@/contexts/LayoutContext";
import { QuickTaskContext } from "@/contexts/QuickTaskContext";
import { useLayoutData } from "@/hooks/useLayoutData";
import { useSideNavState } from "@/hooks/useSideNavState";
import { useWindowOpenerCallbacks } from "@/hooks/useWindowOpenerCallbacks";
import { LoadingPage } from "@/components/common/Loading/LoadingPage";
import { TopBar } from "./TopBar";
import { SideNav } from "./side-nav";
import { QuickTaskPanel, useQuickTaskPanel } from "./quicktask";
import { MegaFooter } from "./MegaFooter";
import { ErrorPage } from "./ErrorPage";
import { AiChatPanel } from "@/components/specific/ai/AiChatPanel";
import { isAskVectaPath } from "@/components/specific/ai/chatNavigation";
import { fetchMenuItems } from "@/api/menu/api";
import type { MenuItem } from "@/types/domain";

export type AppShellProps = {
  children: ReactNode;
}

export const AppShell = ({ children }: AppShellProps) => {
  const { data, loading, error } = useLayoutData();
  const { state: sideNavState, toggle: toggleSideNav } = useSideNavState();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const toggleMobileNav = () => setMobileNavOpen((v) => !v);
  const isFullPageChat = isAskVectaPath(useLocation().pathname);
  const { panelState, hasEverOpened, openPanel, minimisePanel, closePanel } = useQuickTaskPanel();
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [menuError, setMenuError] = useState(false);
  useWindowOpenerCallbacks(data?.appRoutes);

  useEffect(() => {
    fetchMenuItems(window.location.href)
      .then((items) => { setMenuItems(items); setMenuLoading(false); })
      .catch(() => { setMenuError(true); setMenuLoading(false); });
  }, []); 

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingPage message="Loading..." />
      </div>
    );
  }

  if (error || !data) {
    return <ErrorPage />;
  }

  return (
    <LayoutContext.Provider value={{ data, sideNavState, toggleSideNav, mobileNavOpen, toggleMobileNav }}>
      <QuickTaskContext.Provider value={{ openQuickTask: openPanel }}>
        <div
          className={useClassNames("layout-root font-sans", sideNavState === "mini" && "mini", mobileNavOpen && "nav-open")}
        >
          <TopBar />
          <SideNav menuItems={menuItems} menuLoading={menuLoading} menuError={menuError} />
          <main className="layout-page-content">
            <div className={isFullPageChat ? "flex flex-1 flex-col bg-white" : "flex-1 bg-white"}>
              {children}
            </div>
            {!isFullPageChat && <MegaFooter routes={data.appRoutes} />}
          </main>
          <QuickTaskPanel panelState={panelState} hasEverOpened={hasEverOpened} onOpen={openPanel} onMinimise={minimisePanel} onClose={closePanel} />
          {data.isAiChatAvailable && !isFullPageChat && <AiChatPanel showToggle={false} />}
        </div>
      </QuickTaskContext.Provider>
    </LayoutContext.Provider>
  );
}
